import { Ionicons } from '@expo/vector-icons';
import { useRouter } from 'expo-router';
import React from 'react';
import { Pressable, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { AppText } from '@/components/ui/AppText';
import { LiveBadge } from '@/components/ui/LiveBadge';
import { useThemePalette } from '@/config/theme';
import type { FixtureWithRefs } from '@/lib/data/provider';
import { formatKickoffTime } from '@/lib/i18n/format';
import { useLocale } from '@/lib/i18n/LocaleProvider';
import { localizeCompetition, localizeTeamName } from '@/lib/i18n/footballNames';
import { isHotMatch } from '@/lib/utils/popularTeams';

import { AnimatedScore } from './AnimatedScore';
import { TeamCrest } from './TeamCrest';

/** One team: crest beside its name, the crest pinned to the score side. */
function TeamSide({
  name,
  logo,
  align,
}: {
  name: string;
  logo: string | null;
  align: 'start' | 'end';
}) {
  const crest = <TeamCrest uri={logo} name={name} size="sm" />;
  const label = (
    <AppText
      variant="caption"
      weight="semibold"
      className={`shrink text-ink ${align === 'end' ? 'text-end' : 'text-start'}`}
      numberOfLines={1}
    >
      {name}
    </AppText>
  );
  return (
    <View
      className={`flex-1 flex-row items-center gap-2 ${
        align === 'end' ? 'justify-end' : 'justify-start'
      }`}
    >
      {align === 'end' ? label : crest}
      {align === 'end' ? crest : label}
    </View>
  );
}

/**
 * Volt fixture row (FotMob / 365Scores style): both teams' crests flank the
 * centre, which shows the live/finished score or the kickoff time. Replaces the
 * old "single crest + A vs B" layout that hid the opponent's flag.
 */
function FixtureRowImpl({
  fixture,
  hideCompetition = false,
  followed = false,
}: {
  fixture: FixtureWithRefs;
  hideCompetition?: boolean;
  /** A team the user follows / their favourite is playing - gets a heart frame. */
  followed?: boolean;
}) {
  const palette = useThemePalette();
  const router = useRouter();
  const { t } = useTranslation();
  const { numeralOptions, locale } = useLocale();
  const hot = isHotMatch(fixture);
  const kickoffDate = fixture.kickoff_at ? new Date(fixture.kickoff_at) : null;
  const kickoff = kickoffDate && !Number.isNaN(kickoffDate.getTime()) ? kickoffDate : null;

  const center = (() => {
    if (fixture.status === 'live') {
      return (
        <View className="items-center gap-1">
          <AnimatedScore home={fixture.home_score ?? 0} away={fixture.away_score ?? 0} />
          <LiveBadge minute={fixture.minute} />
        </View>
      );
    }
    if (fixture.status === 'finished') {
      return (
        <View className="items-center gap-0.5">
          <AnimatedScore home={fixture.home_score ?? 0} away={fixture.away_score ?? 0} />
          <AppText variant="micro" weight="bold" className="uppercase tracking-wide text-muted">
            {t('common.ft')}
          </AppText>
        </View>
      );
    }
    if (fixture.status === 'postponed') {
      return (
        <AppText variant="micro" weight="medium" className="text-draw">
          {t('common.postponed')}
        </AppText>
      );
    }
    return (
      <View className="items-center gap-0.5">
        <AppText variant="caption" weight="bold" tabular className="text-soft">
          {kickoff ? formatKickoffTime(kickoff, numeralOptions) : '—'}
        </AppText>
        {hideCompetition ? null : (
          <AppText
            variant="micro"
            weight="bold"
            className="uppercase tracking-wide text-muted"
            numberOfLines={1}
          >
            {localizeCompetition(fixture.competition?.name, locale)}
          </AppText>
        )}
      </View>
    );
  })();

  return (
    <Pressable
      accessibilityRole="button"
      onPress={() => router.push(`/match/${fixture.id}`)}
      className={`mx-4 mb-2.5 flex-row items-center gap-2 rounded-card border bg-surface px-3 py-3.5 active:bg-surface-2/60 ${
        followed ? 'border-volt' : hot ? 'border-volt-line' : 'border-line'
      }`}
      style={
        followed
          ? { shadowColor: palette.volt, shadowOpacity: 0.55, shadowRadius: 12, shadowOffset: { width: 0, height: 0 }, borderWidth: 1.5 }
          : hot
            ? { shadowColor: palette.volt, shadowOpacity: 0.45, shadowRadius: 9, shadowOffset: { width: 0, height: 0 } }
            : undefined
      }
    >
      {followed ? (
        <View
          accessibilityLabel={t('matches.yourTeam')}
          className="absolute -top-2 start-3 z-10 h-5 w-5 items-center justify-center rounded-full border-2 border-surface bg-volt"
        >
          <Ionicons name="heart" size={10} color={palette.onVolt} />
        </View>
      ) : hot ? (
        <View
          accessibilityLabel={t('matches.hot')}
          className="absolute -top-2 start-3 z-10 h-5 w-5 items-center justify-center rounded-full border-2 border-surface bg-volt"
        >
          <Ionicons name="star" size={10} color={palette.onVolt} />
        </View>
      ) : null}
      <TeamSide
        name={localizeTeamName(fixture.home_team, locale, true)}
        logo={fixture.home_team?.logo_url ?? null}
        align="end"
      />
      <View className="w-[86px] items-center justify-center">{center}</View>
      <TeamSide
        name={localizeTeamName(fixture.away_team, locale, true)}
        logo={fixture.away_team?.logo_url ?? null}
        align="start"
      />
    </Pressable>
  );
}

/**
 * Memoized: a live-score tick re-renders the matches list's parent, but each
 * row only needs to re-render when its own fixture/followed props change.
 */
export const FixtureRow = React.memo(FixtureRowImpl);
